<script setup lang="ts">
// App-wide toast renderer. Mount ONCE at the app root (App.vue). Reads the
// shared toast store and renders the stack in a corner, teleported to <body>
// so it overlays modals/editors regardless of stacking context.
import { PhCheckCircle, PhWarningCircle, PhInfo, PhX } from '@phosphor-icons/vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'

const toast = useToastStore()
const { t } = useI18n()

const ICONS = {
  success: PhCheckCircle,
  error: PhWarningCircle,
  info: PhInfo,
} as const
</script>

<template>
  <Teleport to="body">
    <div class="toast-host" aria-live="polite" aria-atomic="true">
      <TransitionGroup name="toast">
        <div
          v-for="item in toast.toasts"
          :key="item.id"
          :class="['toast', `toast--${item.type}`]"
          role="status"
        >
          <component :is="ICONS[item.type]" :size="18" weight="fill" class="toast__icon" />
          <span class="toast__msg">{{ item.message }}</span>
          <button type="button" class="toast__close" :aria-label="t('common.dismiss')" @click="toast.dismiss(item.id)">
            <PhX :size="14" weight="bold" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style scoped>
.toast-host {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 100000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: min(92vw, 420px);
  pointer-events: none;
}
.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: var(--radius-md, 8px);
  background: var(--color-surface, #fff);
  border: 1px solid var(--color-border, #e5e7eb);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  font-size: var(--font-size-sm, 14px);
  color: var(--color-text-primary, #111827);
}
.toast__icon { flex-shrink: 0; margin-top: 1px; }
.toast--success .toast__icon { color: #16A34A; }
.toast--error .toast__icon { color: #DC2626; }
.toast--info .toast__icon { color: var(--color-primary-active, #2563EB); }
.toast__msg { flex: 1; line-height: 1.4; }
.toast__close {
  flex-shrink: 0;
  background: transparent;
  border: 0;
  color: var(--color-text-muted, #9CA3AF);
  cursor: pointer;
  padding: 2px;
  display: inline-flex;
}
.toast__close:hover { color: var(--color-text-primary, #111827); }

.toast-enter-active, .toast-leave-active { transition: all 0.22s ease; }
.toast-enter-from { opacity: 0; transform: translateY(10px); }
.toast-leave-to { opacity: 0; transform: translateX(20px); }
</style>
